import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { historyApi } from "../services/api.service";
import { format, subHours } from "date-fns";

// Format date for your API: "YYYY-MM-DD HH:mm:ss"
function toApiDateTime(dateStr) {
  try {
    const d = new Date(dateStr);
    return format(d, "yyyy-MM-dd HH:mm:ss");
  } catch {
    return dateStr;
  }
}

// Normalise API points to internal shape used by the map
function normalizePoints(pts) {
  return pts
    .map(p => ({
      lat:       Number(p.lat),
      lng:       Number(p.lng),
      speed:     Number(p.speed) || 0,
      location:  p.location || "",
      timestamp: p.timestamp,
      heading:   0,
      accuracy:  5,
    }))
    .filter(p => !isNaN(p.lat) && !isNaN(p.lng));
}

// Detect stop points: consecutive points with speed === 0
function computeStops(path) {
  return path.reduce((acc, pt, i) => {
    if (pt.speed === 0 && i > 0 && path[i - 1].speed === 0) return acc;
    if (pt.speed === 0) acc.push({ ...pt, type: "stop" });
    return acc;
  }, []);
}

// Build a per-slot track (path + derived stats + numeric timestamps for sync)
function buildTrack(agent, path, totalDist) {
  const times = path.map(p => {
    const t = Date.parse(p.timestamp);
    return isNaN(t) ? null : t;
  });
  const validTimes = times.filter(t => t !== null);
  const hasTime = validTimes.length >= 2 && validTimes[0] !== validTimes[validTimes.length - 1];
  const avgSpeed = path.length ? +(path.reduce((s, p) => s + p.speed, 0) / path.length).toFixed(1) : 0;
  const maxSpeed = path.length ? Math.max(...path.map(p => p.speed)) : 0;
  return {
    agent,
    path,
    totalDist,
    stops: computeStops(path),
    times,
    hasTime,
    avgSpeed,
    maxSpeed,
    tStart: hasTime ? validTimes[0] : null,
    tEnd:   hasTime ? validTimes[validTimes.length - 1] : null,
  };
}

const EMPTY_TRACK = buildTrack(null, [], 0);

// Index in a track at the given global playback progress (0..1).
// Time-synced when the track has usable timestamps, else index-proportional.
function idxAtProgress(track, progress, gStart, gSpan) {
  const p = track.path;
  if (!p.length) return 0;
  if (track.hasTime && gSpan > 0) {
    const t = gStart + progress * gSpan;
    let idx = 0;
    for (let i = 0; i < p.length; i++) {
      if (track.times[i] != null && track.times[i] <= t) idx = i;
      else if (track.times[i] != null) break;
    }
    return idx;
  }
  return Math.round(progress * (p.length - 1));
}

/**
 * GPS history playback for one or two agents on a single, time-synced timeline.
 * @param {object|Array} agentsInput  single agent, or array of up to 2 agents (nulls allowed)
 */
export function useHistory(agentsInput) {
  const agents = useMemo(
    () => (Array.isArray(agentsInput) ? agentsInput : [agentsInput]).slice(0, 2),
    [agentsInput]
  );
  // Stable identity key so effects only refetch when the actual agents change
  const agentKey = agents.map(a => (a ? a.id : "-")).join("|");

  const now = new Date();
  const [tracks,  setTracks]  = useState([EMPTY_TRACK, EMPTY_TRACK]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Date range — default: last 8 hours (shared across both agents)
  const [dateRange, setDateRange] = useState({
    from: format(subHours(now, 8), "yyyy-MM-dd'T'HH:mm"),
    to:   format(now, "yyyy-MM-dd'T'HH:mm"),
  });

  // Playback — a single normalized progress (0..1) drives every slot
  const [playing,   setPlaying]   = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [playSpeed, setPlaySpeed] = useState(5);
  const playRef = useRef(null);

  // ── Fetch history for every selected agent ────────────────────
  const fetchHistory = useCallback(async () => {
    const list = agents;
    if (!list.some(Boolean)) {
      setTracks([EMPTY_TRACK, EMPTY_TRACK]);
      return;
    }
    setLoading(true);
    setError(null);
    setPlaying(false);
    setProgress(0);

    const fromDateTime = toApiDateTime(dateRange.from);
    const toDateTime   = toApiDateTime(dateRange.to);

    try {
      const results = await Promise.all(
        list.map(async agent => {
          if (!agent) return EMPTY_TRACK;
          // POST /gps-location/gps-history-messages
          const res  = await historyApi.getHistory(agent.id, fromDateTime, toDateTime);
          const pts  = Array.isArray(res.data?.data) ? res.data.data : [];
          const dist = res.data?.totalDistance;
          const path = normalizePoints(pts);
          return buildTrack(agent, path, typeof dist === "number" ? dist : parseFloat(dist) || 0);
        })
      );
      // Always keep two slots so downstream indexing is stable
      setTracks([results[0] || EMPTY_TRACK, results[1] || EMPTY_TRACK]);
    } catch (err) {
      console.error("[History]", err.message);
      setError("Failed to load GPS history");
      setTracks([EMPTY_TRACK, EMPTY_TRACK]);
    } finally {
      setLoading(false);
    }
  }, [agentKey, dateRange]); // eslint-disable-line

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // ── Global timeline spanning all loaded agents ────────────────
  const activeTracks = tracks.filter(t => t.path.length > 0);
  const gStart = activeTracks.length
    ? Math.min(...activeTracks.map(t => (t.tStart != null ? t.tStart : Infinity)).filter(isFinite))
    : 0;
  const gEnd = activeTracks.length
    ? Math.max(...activeTracks.map(t => (t.tEnd != null ? t.tEnd : -Infinity)).filter(isFinite))
    : 0;
  const gSpan = gEnd > gStart ? gEnd - gStart : 0;
  const currentTime = gSpan ? gStart + progress * gSpan : null;

  // Longest path drives the playback resolution / step count
  const maxLen = Math.max(1, ...tracks.map(t => t.path.length));

  // ── Playback engine ───────────────────────────────────────────
  const play  = useCallback(() => { setProgress(p => (p >= 1 ? 0 : p)); setPlaying(true); }, []);
  const pause = useCallback(() => setPlaying(false), []);
  const reset = useCallback(() => { setPlaying(false); setProgress(0); }, []);
  const seekProgress = useCallback(p => setProgress(Math.max(0, Math.min(1, p))), []);
  const nudge   = useCallback(delta => setProgress(p => Math.max(0, Math.min(1, p + delta))), []);
  const toStart = useCallback(() => setProgress(0), []);
  const toEnd   = useCallback(() => setProgress(1), []);

  useEffect(() => {
    if (!playing || maxLen <= 1) { clearInterval(playRef.current); return; }
    clearInterval(playRef.current);
    // Advance so the longest track covers one point per (250/speed) ms — matches prior feel
    const stepPerTick = 1 / (maxLen - 1);
    playRef.current = setInterval(() => {
      setProgress(prev => {
        const next = prev + stepPerTick;
        if (next >= 1) { setPlaying(false); return 1; }
        return next;
      });
    }, Math.max(40, 250 / playSpeed));
    return () => clearInterval(playRef.current);
  }, [playing, playSpeed, maxLen]);

  // ── Per-slot derived playback state ───────────────────────────
  const slots = useMemo(() => tracks.map(track => {
    const idx = idxAtProgress(track, progress, gStart, gSpan);
    const currentPt = track.path[idx] || null;
    return {
      agent:      track.agent,
      path:       track.path,
      stops:      track.stops,
      totalDist:  track.totalDist,
      avgSpeed:   track.avgSpeed,
      maxSpeed:   track.maxSpeed,
      currentIdx: idx,
      currentPt,
      playedPath: track.path.slice(0, idx + 1),
      remainPath: track.path.slice(idx),
    };
  }), [tracks, progress, gStart, gSpan]);

  return {
    slots,
    loading, error,
    dateRange, setDateRange,
    playing, play, pause, reset,
    progress, seekProgress, nudge, toStart, toEnd,
    playSpeed, setPlaySpeed,
    gStart, gEnd, gSpan, currentTime,
    refetch: fetchHistory,
  };
}
